import React, { useMemo, useState } from "react";
import type { EditorElement } from "../../../editor/types";
import { MathMLContent } from "./MathMLContent";

const WIRIS_RENDER_URL = "https://www.wiris.net/demo/editor/render";

function ensureMathMLNamespace(mathml: string): string {
  if (mathml.includes("xmlns")) return mathml;
  return mathml.replace(
    /^<math(?=[\s>])/,
    '<math xmlns="http://www.w3.org/1998/Math/MathML"'
  );
}

/**
 * Extract <math> elements from HTML content (CKEditor wraps in <p> tags).
 * Returns array of clean MathML strings with proper namespace.
 */
function extractMathElements(html: string): string[] {
  if (!html) return [];
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const mathEls = Array.from(tempDiv.querySelectorAll("math"));
  if (mathEls.length > 0) {
    return mathEls.map((el) => ensureMathMLNamespace(el.outerHTML));
  }
  // If the raw string itself starts with <math, use it directly
  const trimmed = html.trim();
  if (trimmed.startsWith("<math")) {
    return [ensureMathMLNamespace(trimmed)];
  }
  return [];
}

function buildWirisImageUrl(mathml: string, color?: string): string {
  const params = new URLSearchParams({ mml: mathml, format: "svg" });
  if (color && color !== "transparent" && color !== "#1f2430") {
    params.set("color", color);
  }
  return `${WIRIS_RENDER_URL}?${params.toString()}`;
}

interface MathTypeElementSimpleProps {
  element: EditorElement;
  mode: "canvas" | "preview";
  onEditClick?: () => void;
}

export const MathTypeElementSimple: React.FC<MathTypeElementSimpleProps> = ({
  element,
  mode,
  onEditClick,
}) => {
  const style = (element.data.style as any) || {};
  const rawMathml = style.mathml || style.latex || "";
  const fontSize = style.fontSize || 18;
  const color = style.color || "#1f2430";
  const [imgError, setImgError] = useState(false);

  const mathParts = useMemo(
    () => extractMathElements(rawMathml),
    [rawMathml]
  );

  const imageUrls = useMemo(
    () => mathParts.map((m) => buildWirisImageUrl(m, color)),
    [mathParts, color]
  );

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    minHeight: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "0",
    padding: "8px",
    boxSizing: "border-box",
    userSelect: "auto",
    overflow: "visible",
    cursor: mode === "canvas" ? "pointer" : "default",
    transition: "all 0.2s ease",
    position: "relative",
  };

  // Show placeholder if no mathml
  if (!rawMathml) {
    return (
      <div
        style={containerStyle}
        onDoubleClick={mode === "canvas" ? onEditClick : undefined}
      >
        <span
          style={{ color: "#9ca3af", fontStyle: "italic", fontSize: "14px" }}
        >
          Matematik ifadesi
        </span>
      </div>
    );
  }

  // Render formula(s) as WIRIS images directly.
  // This bypasses native MathML rendering (broken in Chrome)
  // and MathMLContent pipeline (complex async with sanitization issues).
  if (imageUrls.length > 0 && !imgError) {
    return (
      <div
        style={containerStyle}
        onDoubleClick={mode === "canvas" ? onEditClick : undefined}
      >
        {imageUrls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt="Math formula"
            onError={() => setImgError(true)}
            style={{
              display: "inline-block",
              verticalAlign: "middle",
              maxWidth: "100%",
              maxHeight: "100%",
              height: "auto",
            }}
          />
        ))}
      </div>
    );
  }

  // Fallback: use the robust MathMLContent pipeline.
  return (
    <div style={containerStyle} onDoubleClick={mode === "canvas" ? onEditClick : undefined}>
      <MathMLContent
        html={rawMathml}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: `${fontSize}px`,
          color,
          width: "100%",
        }}
      />
    </div>
  );
};
